import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Icon from '../AppIcon';
import { blogService } from '../../services/blogService';
import { supabase } from '../../lib/supabase';

const ResourceSearch = ({ 
  placeholder = "Search resources, guides, documentation...", 
  initialValue = "",
  showDropdown = true,
  autoFocus = false 
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setSearchTerm(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim().length >= 2) {
      handleFinalizeSearch();
    }
  };

  const handleFinalizeSearch = async () => {
    if (!searchTerm.trim()) return;
    setShowResults(false);
    navigate(`/resources/search?q=${encodeURIComponent(searchTerm.trim())}`);
    
    // Log the finalized search
    supabase.from('search_history').insert([
      { 
        search_term: searchTerm.toLowerCase().trim(),
        results_count: results.length,
        source: 'resource_hub',
        category: 'resource_search'
      }
    ]).then(({ error }) => {
      if (error) console.warn('Failed to log finalized search:', error.message);
    });
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.trim().length >= 2) {
        setLoading(true);
        try {
          const searchData = await blogService.searchPosts(searchTerm);
          setResults(searchData);
          if (showDropdown) {
            setShowResults(true);
          }

          // Record search history if results were found (debounce log)
          if (searchData.length > 0) {
            supabase.from('search_history').insert([
              { 
                search_term: searchTerm.toLowerCase().trim(),
                results_count: searchData.length,
                source: 'resource_hub',
                category: 'resource_debounce'
              }
            ]).then(({ error }) => {
              if (error) console.warn('Failed to log search history:', error.message);
            });
          }
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 500); // Increased debounce to 500ms to avoid over-logging

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  return (
    <div className="relative max-w-2xl mx-auto w-full" ref={searchRef}>
      <div className="relative">
        <Icon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => showDropdown && searchTerm.trim().length >= 2 && setShowResults(true)}
          placeholder={placeholder}
          autoComplete="off"
          autoFocus={autoFocus}
          className="w-full pl-12 pr-12 py-4 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm text-slate-900 bg-white"
        />
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {loading && (
            <Icon name="Loader2" size={18} className="animate-spin text-blue-500" />
          )}
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                setResults([]);
                setShowResults(false);
              }}
              className="text-slate-400 hover:text-slate-600 p-1"
            >
              <Icon name="X" size={18} />
            </button>
          )}
        </div>
      </div>

      {showDropdown && showResults && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden max-h-[400px] overflow-y-auto">
          {results.length > 0 ? (
            <div className="divide-y divide-slate-50">
              {results.map((post) => (
                <Link
                  key={post.id}
                  to={`/${post.target_page || post.targetPage || 'blog'}/${post.slug}`}
                  onClick={() => setShowResults(false)}
                  className="block p-4 hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-500 flex-shrink-0">
                      <Icon name="FileText" size={16} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors line-clamp-1">
                        {post.title}
                      </h4>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center mt-2 space-x-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                          {post.targetPage?.replace('-', ' ')}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {post.category?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500 italic">
              No matching articles found for "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResourceSearch;
